import React from 'react'
import { CalendarOutlined } from '@ant-design/icons'
import { DatePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppService } from '../../../../services/app'
import { DateWindow, IsoDate, lengthInDays } from '../../math/windows'
import { MAX_CUSTOM_RANGE_DAYS } from '../../query/use-org-dashboard-query'
import {
    ORG_DASHBOARD_PRESET_LABELS,
    OrgDashboardRangeKey,
    buildOrgDashboardTimeRangePresets,
    resolveRangeKey,
    ORG_DASHBOARD_RANGE_KEYS,
} from '../../query/org-dashboard-presets'
import '../../components/org-dashboard.scss'

type Props = {
    range: DateWindow
    rangeKey: OrgDashboardRangeKey | 'custom'
    latestCompleteDate: IsoDate
    onSelectPreset: (key: OrgDashboardRangeKey) => void
    onSelectCustomRange: (dateWindow: DateWindow) => void
}

const ISO_DATE_FORMAT = 'YYYY-MM-DD'

/** The preset whose dates are exactly this window, so choosing those dates by hand reads as the preset. */
export const presetKeyMatching = (
    dateWindow: DateWindow,
    latestCompleteDate: IsoDate,
): OrgDashboardRangeKey | undefined =>
    ORG_DASHBOARD_RANGE_KEYS.find((key) => {
        const resolved = resolveRangeKey(key, latestCompleteDate)
        return resolved.since === dateWindow.since && resolved.through === dateWindow.through
    })

/**
 * A date can be picked when it is not after the latest complete date and, once one end of a custom range is
 * chosen, when it keeps the range within the longest span the dashboard reads.
 */
export const isDateSelectable = (date: Dayjs, latestCompleteDate: IsoDate, otherEnd?: Dayjs | null): boolean => {
    const isoDate = date.format(ISO_DATE_FORMAT)
    if (isoDate > latestCompleteDate) return false
    if (!otherEnd) return true
    const otherEndIsoDate = otherEnd.format(ISO_DATE_FORMAT)
    const [since, through] = isoDate < otherEndIsoDate ? [isoDate, otherEndIsoDate] : [otherEndIsoDate, isoDate]
    return lengthInDays({ since, through }) <= MAX_CUSTOM_RANGE_DAYS
}

export const DateRangeControl: React.FC<Props> = ({
    range,
    rangeKey,
    latestCompleteDate,
    onSelectPreset,
    onSelectCustomRange,
}) => {
    const appService = useService(AppService)
    const throughDate = dayjs(range.through)

    /** The year appears once, on the last date, so the closed control reads "Oct 3 – Oct 9, 2026". */
    const formatDate = (date: Dayjs) =>
        date.isSame(throughDate, 'day') || date.year() !== throughDate.year()
            ? date.format('MMM D, YYYY')
            : date.format('MMM D')

    const commit = (dates: Array<Dayjs | null> | null) => {
        if (!dates?.[0] || !dates?.[1]) return
        const chosen = { since: dates[0].format(ISO_DATE_FORMAT), through: dates[1].format(ISO_DATE_FORMAT) }
        const matchingPresetKey = presetKeyMatching(chosen, latestCompleteDate)
        if (matchingPresetKey) onSelectPreset(matchingPresetKey)
        else onSelectCustomRange(chosen)
    }

    /** Each field is as wide as the date it shows; the stylesheet turns these character counts into widths. */
    const fieldWidths = {
        '--org-dashboard-date-start-characters': formatDate(dayjs(range.since)).length,
        '--org-dashboard-date-end-characters': formatDate(throughDate).length,
    } as React.CSSProperties

    return (
        <DatePicker.RangePicker
            size="middle"
            className="org-dashboard-date-range"
            style={fieldWidths}
            allowClear={false}
            separator="–"
            format={formatDate}
            getPopupContainer={appService.getAppContainer}
            presets={buildOrgDashboardTimeRangePresets(latestCompleteDate)}
            prefix={
                <span className="org-dashboard-date-range-prefix">
                    <CalendarOutlined />
                    {rangeKey !== 'custom' && <span>{ORG_DASHBOARD_PRESET_LABELS[rangeKey]}</span>}
                </span>
            }
            value={[dayjs(range.since), throughDate]}
            disabledDate={(date, info) => !isDateSelectable(date, latestCompleteDate, info.from)}
            onChange={commit}
        />
    )
}
